import { useEffect, useRef, useState } from "hono/jsx";

export interface UseVoxholeConnectionProps {
  roomId: string;
  user?: { id: string; isAnonymous?: boolean };
}

export function useVoxholeConnection(
  { roomId, user }: UseVoxholeConnectionProps,
) {
  const [connected, setConnected] = useState(false);
  const [roomNotFound, setRoomNotFound] = useState<
    boolean | { found: false; isReserved?: boolean; isOwner?: boolean }
  >(false);
  // deno-lint-ignore no-explicit-any
  const [initData, setInitData] = useState<any>(null);
  const [isHost, setIsHost] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [banMessage, setBanMessage] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const isBannedRef = useRef(false);

  useEffect(() => {
    if (typeof globalThis.window === "undefined") return;
    if (!roomId) return;

    // Singleton Guard
    const globalKey = `__vox_room_${roomId}`;
    // deno-lint-ignore no-explicit-any
    const existingInfo = (globalThis as any)[globalKey];
    if (
      existingInfo && existingInfo.connected &&
      Date.now() - existingInfo.timestamp < 1000
    ) {
      setIsDuplicate(true);
      return;
    }
    // deno-lint-ignore no-explicit-any
    (globalThis as any)[globalKey] = { connected: true, timestamp: Date.now() };

    if (wsRef.current) wsRef.current.close();

    const protocol = globalThis.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = new URL(
      `${protocol}//${globalThis.location.host}/ws/voxhole`,
    );
    wsUrl.searchParams.set("room", roomId);
    if (user?.id) {
      wsUrl.searchParams.set("playerId", user.id);
    } else {
      const storedGuestId = sessionStorage.getItem("vox:guestId");
      if (storedGuestId) wsUrl.searchParams.set("playerId", storedGuestId);
    }

    const ws = new WebSocket(wsUrl.toString());
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      setIsDuplicate(false);
      isBannedRef.current = false;
    };

    ws.onclose = (e: CloseEvent) => {
      // deno-lint-ignore no-explicit-any
      if ((globalThis as any)[globalKey]) {
        (globalThis as any)[globalKey].connected = false;
      }
      setConnected(false);
      setInitData(null);

      // Code 4003 or 1008 = Policy Violation/Ban
      if (e.code === 4003 || e.code === 1008) {
        isBannedRef.current = true;
        setBanMessage("Banned from room (Connection Rejected)");
      }

      if (!isBannedRef.current) {
        setTimeout(() => setRetryCount((c) => c + 1), 2000);
      }
    };

    const handleMessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === "ROOM_NOT_FOUND") {
          setRoomNotFound({
            found: false,
            isReserved: msg.isReserved,
            isOwner: msg.isOwner,
          });
        } else if (msg.type === "BANNED" || msg.type === "KICKED") {
          const reason = msg.reason ||
            "You have been kicked/banned from this room.";
          setBanMessage(reason);
          isBannedRef.current = true;
          ws.close();
        } else if (msg.type === "INIT") {
          setRoomNotFound(false);
          setIsHost(msg.isHost || false);
          setInitData(msg);
        }

        document.dispatchEvent(
          new CustomEvent("vox:ws:message", { detail: msg }),
        );
      } catch (e) {
        console.error("Failed to parse WS message", e);
      }
    };

    ws.addEventListener("message", handleMessage);

    return () => {
      // deno-lint-ignore no-explicit-any
      if ((globalThis as any)[globalKey]) {
        (globalThis as any)[globalKey].connected = false;
      }
      ws.onclose = null;
      ws.removeEventListener("message", handleMessage);
      ws.close();
      setConnected(false);
    };
  }, [roomId, retryCount, user?.id]);

  useEffect(() => {
    const handleHostUpdate = (e: CustomEvent) => setIsHost(!!e.detail);
    document.addEventListener(
      "vox:host:update",
      handleHostUpdate as EventListener,
    );
    return () =>
      document.removeEventListener(
        "vox:host:update",
        handleHostUpdate as EventListener,
      );
  }, []);

  useEffect(() => {
    const onBanned = (e: CustomEvent) => {
      setBanMessage(e.detail);
      isBannedRef.current = true;
    };
    document.addEventListener("vox:banned", onBanned as EventListener);
    return () =>
      document.removeEventListener("vox:banned", onBanned as EventListener);
  }, []);

  return {
    connected,
    roomNotFound,
    setRoomNotFound, // Exposed for retry logic
    initData,
    isHost,
    isDuplicate,
    banMessage,
    wsRef,
    retryCount,
    setRetryCount,
  };
}
